import React, { useCallback, useEffect, useRef, useState } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../ui/dialog";
import { toast } from "sonner";
import {
  FileText, Upload, Trash2, CheckCircle2, Eye, Loader2, AlertTriangle, Image as ImageIcon,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MB = 1024 * 1024;

function fmtSize(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < MB) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / MB).toFixed(2)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("nl-NL"); } catch { return iso; }
}

function MimeBadge({ mime }) {
  if (!mime) return null;
  const isPdf = mime === "application/pdf";
  const Icon = isPdf ? FileText : ImageIcon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-chivo font-bold ${isPdf ? "bg-red-100 text-red-700" : "bg-indigo-100 text-indigo-700"}`}>
      <Icon className="w-3 h-3" /> {mime.split("/").pop().toUpperCase()}
    </span>
  );
}

export default function LetterheadsSection() {
  const { user, hasPermission } = useAuth();
  const isOwnerOrAdmin = user?.role === "owner" || user?.role === "admin";
  const isManager = user?.role === "manager";
  const canEdit = isOwnerOrAdmin;
  const canView = isOwnerOrAdmin || isManager;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState({
    letterheads: [],
    allowed_doc_types: ["algemeen", "offerte", "factuur", "paklijst", "boekingsformulier"],
    doc_type_labels: {},
    active_by_doc_type: {},
    max_size_bytes: 10 * MB,
    allowed_mime_types: ["application/pdf", "image/png", "image/jpeg"],
  });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadDocType, setUploadDocType] = useState("algemeen");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadActivate, setUploadActivate] = useState(true);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [rowBusy, setRowBusy] = useState(null);
  const fileInputRef = useRef(null);

  const reload = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await adminApi.get("/admin/letterheads");
      setData(r.data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (canView) reload(); else setLoading(false); }, [canView, reload]);

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error("Kies eerst een bestand");
      return;
    }
    if (uploadFile.size > data.max_size_bytes) {
      toast.error(`Bestand te groot — max ${Math.round(data.max_size_bytes / MB)} MB`);
      return;
    }
    setUploadBusy(true);
    try {
      const form = new FormData();
      form.append("file", uploadFile);
      const r = await adminApi.post(
        `/admin/letterheads/upload?doc_type=${encodeURIComponent(uploadDocType)}&activate=${uploadActivate ? "true" : "false"}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(`Briefpapier voor ${data.doc_type_labels[r.data.doc_type] || r.data.doc_type} geüpload`);
      setUploadOpen(false);
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploadBusy(false);
    }
  };

  const handleActivate = async (lh) => {
    setRowBusy(lh.id);
    try {
      await adminApi.post(`/admin/letterheads/${lh.id}/activate`);
      toast.success(`Geactiveerd voor ${data.doc_type_labels[lh.doc_type] || lh.doc_type}`);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRowBusy(null);
    }
  };

  const handleDeactivate = async (lh) => {
    setRowBusy(lh.id);
    try {
      await adminApi.post(`/admin/letterheads/${lh.id}/deactivate`);
      toast.success("Briefpapier gedeactiveerd");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRowBusy(null);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setRowBusy(confirmDelete.id);
    try {
      await adminApi.delete(`/admin/letterheads/${confirmDelete.id}`);
      toast.success("Briefpapier verwijderd");
      setConfirmDelete(null);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRowBusy(null);
    }
  };

  const handlePreview = async (lh) => {
    // Use a one-shot token fetch + blob URL so the Authorization header
    // reaches the backend (window.open can't set headers).
    try {
      const r = await adminApi.get(`/admin/letterheads/${lh.id}/file`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: lh.mime_type });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!canView) {
    return (
      <div className="bg-white border border-gray-200 p-6" data-testid="letterheads-no-access">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
          <div>
            <p className="font-chivo font-bold text-[#0A192F]">Geen toegang</p>
            <p className="text-sm text-gray-600 mt-1">
              Briefpapier-beheer is alleen beschikbaar voor Owner, Admin en Manager.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Group letterheads by doc_type for display
  const byDocType = {};
  for (const dt of data.allowed_doc_types) byDocType[dt] = [];
  for (const lh of data.letterheads || []) {
    if (byDocType[lh.doc_type]) byDocType[lh.doc_type].push(lh);
  }

  return (
    <div className="space-y-6" data-testid="letterheads-section">
      {/* Header */}
      <div className="bg-white border border-gray-200 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-chivo font-bold text-lg text-[#0A192F] flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#0055ff]" /> Briefpapier (PDF-achtergrond)
            </h2>
            <p className="text-sm text-gray-600 mt-1 max-w-2xl">
              Upload uw briefpapier (PDF, PNG of JPG, max {Math.round(data.max_size_bytes / MB)} MB).
              Het wordt als achtergrond op pagina 1 van gegenereerde PDFs gezet
              (offerte nu, factuur/paklijst/boekingsformulier later).
              Eén actief briefpapier per documenttype. <b>Algemeen</b> wordt gebruikt als fallback.
            </p>
          </div>
          {canEdit && (
            <Button
              data-testid="letterheads-upload-btn"
              onClick={() => setUploadOpen(true)}
              className="bg-[#0055ff] hover:bg-[#0044cc] text-white rounded-none"
            >
              <Upload className="w-4 h-4 mr-2" /> Briefpapier uploaden
            </Button>
          )}
        </div>
        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">{error}</div>
        )}
      </div>

      {/* Grouped per doc_type */}
      {loading ? (
        <div className="bg-white border border-gray-200 p-8 flex items-center justify-center gap-2 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" /> Laden…
        </div>
      ) : (
        <div className="space-y-4">
          {data.allowed_doc_types.map((dt) => {
            const rows = byDocType[dt] || [];
            const activeId = data.active_by_doc_type?.[dt];
            return (
              <div key={dt} className="bg-white border border-gray-200" data-testid={`letterheads-group-${dt}`}>
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
                  <div className="font-chivo font-bold text-[#0A192F] text-sm">
                    {data.doc_type_labels?.[dt] || dt}
                    {dt === "algemeen" && (
                      <span className="ml-2 text-[11px] font-normal text-gray-500">— fallback voor andere documenttypes</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {activeId ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Actief
                      </span>
                    ) : (
                      <span className="text-gray-400">Geen actief briefpapier</span>
                    )}
                  </div>
                </div>
                {rows.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500">
                    Nog geen briefpapier voor dit documenttype. {dt !== "algemeen" && "Het algemene briefpapier wordt gebruikt als fallback."}
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                      <tr>
                        <th className="text-left px-4 py-2">Bestandsnaam</th>
                        <th className="text-left px-4 py-2">Type</th>
                        <th className="text-left px-4 py-2">Grootte</th>
                        <th className="text-left px-4 py-2">Geüpload</th>
                        <th className="text-left px-4 py-2">Status</th>
                        <th className="text-right px-4 py-2">Acties</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((lh) => (
                        <tr key={lh.id} className="border-t border-gray-100" data-testid={`letterheads-row-${lh.id}`}>
                          <td className="px-4 py-3 font-mono text-xs text-[#0A192F] break-all">{lh.filename}</td>
                          <td className="px-4 py-3"><MimeBadge mime={lh.mime_type} /></td>
                          <td className="px-4 py-3 text-gray-600">{fmtSize(lh.size_bytes)}</td>
                          <td className="px-4 py-3 text-gray-600 text-xs">
                            <div>{fmtDate(lh.uploaded_at)}</div>
                            <div className="text-[11px] text-gray-400">{lh.uploaded_by}</div>
                          </td>
                          <td className="px-4 py-3">
                            {lh.active ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[11px] font-chivo font-bold">
                                <CheckCircle2 className="w-3 h-3" /> Actief
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-[11px]">
                                Inactief
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1 justify-end flex-wrap">
                              <Button
                                variant="ghost" size="sm"
                                className="rounded-none h-7 px-2"
                                data-testid={`letterheads-preview-${lh.id}`}
                                onClick={() => handlePreview(lh)}
                                disabled={rowBusy === lh.id}
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                              {canEdit && !lh.active && (
                                <Button
                                  variant="outline" size="sm"
                                  className="rounded-none h-7 px-2 text-xs"
                                  data-testid={`letterheads-activate-${lh.id}`}
                                  onClick={() => handleActivate(lh)}
                                  disabled={rowBusy === lh.id}
                                >
                                  Activeer
                                </Button>
                              )}
                              {canEdit && lh.active && (
                                <Button
                                  variant="outline" size="sm"
                                  className="rounded-none h-7 px-2 text-xs"
                                  data-testid={`letterheads-deactivate-${lh.id}`}
                                  onClick={() => handleDeactivate(lh)}
                                  disabled={rowBusy === lh.id}
                                >
                                  Deactiveer
                                </Button>
                              )}
                              {canEdit && (
                                <Button
                                  variant="ghost" size="sm"
                                  className="rounded-none h-7 px-2 text-red-600 hover:bg-red-50 hover:text-red-700"
                                  data-testid={`letterheads-delete-${lh.id}`}
                                  onClick={() => setConfirmDelete(lh)}
                                  disabled={rowBusy === lh.id}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={(o) => { if (!uploadBusy) setUploadOpen(o); }}>
        <DialogContent data-testid="letterheads-upload-dialog">
          <DialogHeader>
            <DialogTitle>Briefpapier uploaden</DialogTitle>
            <DialogDescription>
              Kies het documenttype + bestand (PDF/PNG/JPG, max {Math.round(data.max_size_bytes / MB)} MB).
              Pagina 1 van uw briefpapier wordt als achtergrond op pagina 1 van het document gebruikt.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Documenttype</Label>
              <Select value={uploadDocType} onValueChange={setUploadDocType}>
                <SelectTrigger data-testid="letterheads-upload-doctype" className="rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {data.allowed_doc_types.map((dt) => (
                    <SelectItem key={dt} value={dt}>
                      {data.doc_type_labels?.[dt] || dt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Bestand</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                data-testid="letterheads-upload-file"
                className="block w-full text-sm border border-gray-300 file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-[#0055ff] file:text-white file:text-xs file:font-chivo file:font-bold file:hover:bg-[#0044cc]"
              />
              {uploadFile && (
                <p className="text-xs text-gray-500 mt-1">{uploadFile.name} — {fmtSize(uploadFile.size)}</p>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={uploadActivate}
                onChange={(e) => setUploadActivate(e.target.checked)}
                data-testid="letterheads-upload-activate"
              />
              Direct activeren voor {data.doc_type_labels?.[uploadDocType] || uploadDocType}
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="rounded-none" onClick={() => setUploadOpen(false)} disabled={uploadBusy}>
              Annuleren
            </Button>
            <Button
              onClick={handleUpload}
              disabled={uploadBusy || !uploadFile}
              data-testid="letterheads-upload-submit"
              className="bg-[#0055ff] hover:bg-[#0044cc] text-white rounded-none"
            >
              {uploadBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Uploaden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <DialogContent data-testid="letterheads-delete-dialog">
          <DialogHeader>
            <DialogTitle>Briefpapier verwijderen?</DialogTitle>
            <DialogDescription>
              Hiermee verwijder je{" "}
              <span className="font-mono">{confirmDelete?.filename}</span> uit het briefpapier-beheer.
              Het bestand wordt definitief verwijderd. Deze actie kan niet ongedaan gemaakt worden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" className="rounded-none" onClick={() => setConfirmDelete(null)}>Annuleren</Button>
            <Button
              variant="destructive"
              className="rounded-none"
              onClick={handleDelete}
              data-testid="letterheads-delete-confirm"
            >
              Definitief verwijderen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
